/**
 * Dims what will not be exported while an area is being selected, so the panel
 * count and the map agree at a glance.
 *
 * Three states, matching the agreed design:
 *   exporting          full colour
 *   outside the box    dimmed, keeps its own colour
 *   type switched off  dimmed as well
 *
 * Measurements are reached through their own `mapObj()` (tracks, lines,
 * polygons and circles all set one). Markers do not have a `mapObj`, so they
 * are reached through the shared clusterer's element list — identity is not
 * needed, only whether the element's position falls inside the box.
 *
 * Original opacities are remembered per map object the first time it is dimmed
 * and restored from there, so nothing is guessed on the way back.
 */

import sharedCluster from '../../../../map/sharedMarkerCluster'
import { pointInBounds } from './export-geometry'

// Excluded objects have to stay readable against satellite imagery, so they are
// drained of colour rather than made nearly transparent. Markers are small and
// disappear entirely below roughly 0.4, so they are held higher than the lines
// and fills, which stay legible much darker.
const DIM_MARKER_OPACITY = 0.7
// Colour, not transparency, is what marks a shape as excluded — so strokes stay
// close to solid. Fading as well as draining made thin lines disappear.
const DIM_STROKE = 0.9
const DIM_FILL = 0.2

// Markers are DOM, so greyscale is a CSS filter. Lines, polygons and circles
// are vector overlays drawn by the Maps API — no element to filter — so the
// colour has to be swapped for a neutral instead. A dark grey rather than a
// light one: satellite imagery is busy and mid-toned, and a pale line laid over
// it reads as absent. $color-secondary from _vars.
const DIM_COLOR = '#4f4f4f'

const originals = new WeakMap()

function mapObjectFor(measure) {
  if (!measure || typeof measure.mapObj !== 'function') return null
  let obj = null
  try {
    obj = measure.mapObj()
  } catch (e) {
    return null
  }
  return obj && typeof obj.setOptions === 'function' ? obj : null
}

/**
 * Dotted and dashed tracks are drawn with strokeOpacity 0 and rendered entirely
 * through `icons` (see trackPolylineOptions in lib/trackColor.js), each icon
 * carrying its own opacity. Fading the polyline's stroke does nothing to those,
 * so the symbols have to be faded too or a dashed track never dims.
 */
function dimIcons(icons) {
  if (!Array.isArray(icons) || !icons.length) return icons
  return icons.map(entry => {
    if (!entry || !entry.icon) return entry
    const icon = entry.icon
    const s = icon.strokeOpacity == null ? 1 : icon.strokeOpacity
    const f = icon.fillOpacity == null ? 0 : icon.fillOpacity
    return Object.assign({}, entry, {
      icon: Object.assign({}, icon, {
        strokeColor: icon.strokeColor == null ? undefined : DIM_COLOR,
        fillColor: icon.fillColor == null ? undefined : DIM_COLOR,
        strokeOpacity: s * DIM_STROKE,
        fillOpacity: f * DIM_STROKE
      })
    })
  })
}

function styleMeasure(measure, dim) {
  const obj = mapObjectFor(measure)
  if (!obj) return

  if (!originals.has(obj)) {
    originals.set(obj, {
      strokeOpacity: obj.get('strokeOpacity'),
      fillOpacity: obj.get('fillOpacity'),
      strokeColor: obj.get('strokeColor'),
      fillColor: obj.get('fillColor'),
      icons: obj.get('icons')
    })
  }
  const base = originals.get(obj)
  const stroke = base.strokeOpacity == null ? 1 : base.strokeOpacity
  const fill = base.fillOpacity == null ? 0 : base.fillOpacity

  obj.setOptions(dim
    ? {
      strokeColor: DIM_COLOR,
      fillColor: DIM_COLOR,
      strokeOpacity: stroke * DIM_STROKE,
      fillOpacity: fill * DIM_FILL,
      icons: dimIcons(base.icons)
    }
    : {
      strokeColor: base.strokeColor,
      fillColor: base.fillColor,
      strokeOpacity: stroke,
      fillOpacity: fill,
      icons: base.icons
    })
}

function latLngOf(marker) {
  const pos = marker && marker.position
  if (!pos) return null
  const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat
  const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

function styleMarkerElements(list, bounds, wanted) {
  (list || []).forEach(marker => {
    const el = marker && marker.content
    if (!el || !el.style) return

    let dim = true
    if (wanted && bounds) {
      const at = latLngOf(marker)
      dim = !(at && pointInBounds(at.lng, at.lat, bounds))
    } else if (wanted && !bounds) {
      dim = false
    }
    // Greyscale carries "excluded" here; the opacity only softens it, so the
    // pin stays findable on imagery instead of washing out.
    el.style.opacity = dim ? String(DIM_MARKER_OPACITY) : ''
    el.style.filter = dim ? 'grayscale(1)' : ''
  })
}

function measuresOf(props) {
  const { tracks = [], lines = [], polygons = [], circles = [] } = props
  return { tracks, shapes: [].concat(lines, polygons, circles) }
}

/**
 * @param bounds    the selected area, or null when nothing is selected
 * @param props     the redux collections
 * @param selection the result of selectWithin — what is actually exporting
 * @param enabled   { markers, tracks, shapes } type toggles
 */
export function applyHighlight(bounds, props, selection, enabled) {
  if (!bounds) {
    clearHighlight(props)
    return
  }

  const { tracks, shapes } = measuresOf(props)
  const keep = new Set(selection ? selection.measurements : [])

  tracks.forEach(t => styleMeasure(t, !enabled.tracks || !keep.has(t)))
  shapes.forEach(s => styleMeasure(s, !enabled.shapes || !keep.has(s)))
  styleMarkerElements(sharedCluster.regularMarkers, bounds, enabled.markers)
  styleMarkerElements(sharedCluster.photoMarkers, bounds, enabled.photos)
}

/** Put everything back the way the map drew it. */
export function clearHighlight(props) {
  const { tracks, shapes } = measuresOf(props)
  tracks.forEach(t => styleMeasure(t, false))
  shapes.forEach(s => styleMeasure(s, false))
  styleMarkerElements(sharedCluster.regularMarkers, null, true)
  styleMarkerElements(sharedCluster.photoMarkers, null, true)
}
